/* ── QR scan payloads ────────────────────────────────────────────────────────
 * Printed asset labels encode the deep link built by assetDeepLink():
 *
 *     https://app.smileseasons.com/manager/assets/<id>?u=<unique_id>
 *
 * Stickers already in the field predate the `u=` param, and some assets can't
 * take a sticker at all (the ID gets written on by hand). So the parser accepts
 * everything we have ever printed or might be typed in, and it is deliberately
 * lenient about the origin — a label printed from a dev/preview host still has
 * to scan on the production app.
 */

/**
 * @param {string} text raw QR payload / manual entry
 * @returns {{ id:number, uid:string }|null} null when it isn't one of ours
 */
export function parseScan(text) {
  const raw = String(text || '').trim()
  if (!raw) return null

  // 1. A deep link (any origin) — the normal case.
  const m = raw.match(/\/manager\/assets\/(\d+)/)
  if (m) {
    let uid = ''
    try { uid = new URL(raw, 'https://app.smileseasons.com').searchParams.get('u') || '' } catch { uid = '' }
    if (!uid) {
      const um = raw.match(/[?&]u=([^&\s]+)/)
      if (um) { try { uid = decodeURIComponent(um[1]) } catch { uid = um[1] } }
    }
    return { id: Number(m[1]), uid }
  }

  // 2. A bare id, or the "ASSET #123" line printed under the QR.
  const bare = raw.match(/^(?:asset\s*#?\s*)?(\d{1,9})$/i)
  if (bare) return { id: Number(bare[1]), uid: '' }

  return null
}

/* ── sticker verification ────────────────────────────────────────────────────
 * `u` is the unique_id minted by Xano at insert. Comparing it to the record we
 * just loaded catches the two ways a label lies: a sticker peeled off one unit
 * and stuck on another, and a photocopied/duplicated label. A label with no `u`
 * is simply older than the param — that is not a mismatch, just unverified.
 *
 * → 'ok' | 'mismatch' | 'unverified' */
export function verifyScan(asset, u) {
  const scanned = String(u || '').trim()
  const actual = String(asset?.unique_id || '').trim()
  if (!scanned || !actual) return 'unverified'
  return scanned.toLowerCase() === actual.toLowerCase() ? 'ok' : 'mismatch'
}

// Where a successful scan should land.
export function checkPath({ id, uid }) {
  return `/manager/assets/${id}/check${uid ? `?u=${encodeURIComponent(uid)}` : ''}`
}
